import React, { useEffect, useMemo, useRef, useState } from "react";
import fixGrammar from "../../Asset/Fix Grammar.svg";
import professional from "../../Asset/Professional.svg";
import formatText from "../../Asset/Format Text.svg";
import transcribeOnly from "../../Asset/Transcribe Only.svg";
import askGPT from "../../Asset/Ask GPT1.svg";
import rephrase from "../../Asset/Rephrase.svg";
import casual from "../../Asset/Casual.svg";
import organizeThoughts from "../../Asset/Organize Thoughts.svg";
import customPromptIcon from "../../Asset/CustomPrompt_blue.svg";
import axios from "axios";
import { toast } from "react-toastify";
import Sidebar from "../../component/sidebar/sidebar";
import MainTextBoxPanel from "./components/MainTextBoxPanel";
import UsagePreferenceCard from "./components/UsagePreferenceCard";
import OutputLanguageCard from "./components/OutputLanguageCard";
import AppTopBar from "../../component/AppTopBar/AppTopBar";
import SettingsPanel from "./components/SettingsPanel";
import DictionaryPanel from "./components/DictionaryPanel";
import TranscriptHistoryPanel from "./components/TranscriptHistoryPanel";
import {
  trackEvent,
  trackFirstDictationCompletedOnce,
} from "../../lib/mixpanel";
import { getRealtimeRelayBaseUrl } from "../../lib/realtimeRelay";
import {
  buildRealtimeSocketUrl,
  getRealtimeSocketToken,
} from "../../lib/realtimeSocketAuth";
import {
  createSecureContextError,
  createUsageLimitError,
  formatAppError,
  isUsageLimitSignal,
  USAGE_LIMIT_ERROR_CODE,
  USAGE_LIMIT_MESSAGE,
} from "../../lib/appErrors";
import { useUsage } from "../../context/UsageContext";

/* =========================
   Config
========================= */
const sortPromptOptionsForDisplay = (prompts) =>
  [...prompts].sort((a, b) => {
    const aDefault = a?.isDefaultPrompt === true ? 1 : 0;
    const bDefault = b?.isDefaultPrompt === true ? 1 : 0;
    if (aDefault !== bDefault) return bDefault - aDefault;

    return String(a?.label || "").localeCompare(String(b?.label || ""), undefined, {
      sensitivity: "base",
    });
  });

const DEFAULT_PROMPT_OPTIONS = sortPromptOptionsForDisplay([
  { label: "Fix Grammar", icon: fixGrammar, bgColor: "#05280826", promptText: "" },
  { label: "Professional", icon: professional, bgColor: "#7E5C4926", promptText: "" },
  { label: "Format Text", icon: formatText, bgColor: "#D4633F26", promptText: "" },
  { label: "Transcribe Only", icon: transcribeOnly, bgColor: "#3432D726", promptText: "" },
  { label: "Ask GPT", icon: askGPT, bgColor: "#3AB1F624", promptText: "" },
  { label: "Rephrase", icon: rephrase, bgColor: "#7EA3C426", promptText: "" },
  { label: "Casual", icon: casual, bgColor: "#FFBC2926", promptText: "" },
  { label: "Organize Thoughts", icon: organizeThoughts, bgColor: "#FF9BBA26", promptText: "" },
]);

const PLACEHOLDER_PROMPT_ICON = customPromptIcon;
const CUSTOM_PROMPT_BG = "#3AB1F624";
const PROMPT_ICON_BY_LABEL = {
  "fix grammar": fixGrammar,
  professional: professional,
  "format text": formatText,
  "transcribe only": transcribeOnly,
  "transcript only": transcribeOnly,
  "ask gpt": askGPT,
  rephrase: rephrase,
  casual: casual,
  "organize thoughts": organizeThoughts,
};
const PROMPT_BG_BY_LABEL = {
  "fix grammar": "#05280826",
  professional: "#7E5C4926",
  "format text": "#D4633F26",
  "transcribe only": "#3432D726",
  "transcript only": "#3432D726",
  "ask gpt": "#3AB1F624",
  rephrase: "#7EA3C426",
  casual: "#FFBC2926",
  "organize thoughts": "#FF9BBA26",
};

const getPromptIcon = ({ label, isSystemGenerated }) => {
  if (isSystemGenerated === false) return PLACEHOLDER_PROMPT_ICON;
  const key = String(label || "").trim().toLowerCase();
  return PROMPT_ICON_BY_LABEL[key] || PLACEHOLDER_PROMPT_ICON;
};

const pickPromptBg = (index, label = "") => {
  const key = String(label || "").trim().toLowerCase();
  if (PROMPT_BG_BY_LABEL[key]) return PROMPT_BG_BY_LABEL[key];
  const palette = [
    "#05280826",
    "#7E5C4926",
    "#D4633F26",
    "#3432D726",
    "#3AB1F624",
    "#7EA3C426",
    "#FFBC2926",
    "#FF9BBA26",
  ];
  return palette[index % palette.length];
};

const normalizePromptLabel = (value) => String(value || "").trim().toLowerCase();
const isTranscribeOnlyPrompt = (value) => {
  const normalized = normalizePromptLabel(value);
  return normalized === "transcribe only" || normalized === "transcript only";
};

const getFallbackPromptText = (promptType) => {
  switch (String(promptType || "").trim()) {
    case "Casual":
      return "prompt: You are a writing assistant that rewrites text in a relaxed, friendly, and conversational tone. Use natural phrasing and everyday language. Make it sound human and informal. Return only the rewritten casual version - no commentary.";
    case "Professional":
      return "prompt: You are a writing assistant that rewrites text in a formal, professional tone suitable for business communication, customer emails, or job-related tasks. Improve clarity and tone. Return only the professionally rewritten text - no explanations.";
    case "Fix Grammar":
      return "prompt: You are a text correction assistant. Your task is to correct user-submitted text. Under no circumstances should you provide answers, explanations, or additional context. Fix grammar and punctuation using English spelling while preserving the original tone. Format the text if needed by converting lists into bullet points, numbered lists, or well-structured paragraphs. Convert written numbers and currency expressions into numerical and symbolic form when appropriate (e.g., \"five hundred pounds\" -> \"GBP 500\", \"one to two days\" -> \"1-2 days\"). Never use semicolons. Insert a blank line after any greeting and before any closing thank-you message, if present. Do not add a thank-you message unless it already exists in the original input. If no corrections are needed, return the input text exactly as it is. Return only the corrected text. Do not include explanations, quotations, or any commentary.";
    case "Ask GPT":
      return "prompt: You are a smart, conversational AI assistant. Respond to the user's message naturally and intelligently. Answer questions, give suggestions, or continue the user's thought in a meaningful way. Provide helpful, relevant replies without explanations unless explicitly asked.";
    case "Format Text":
      return "prompt: You are a formatting assistant. Fix spelling and format the text if needed by converting lists into bullet points, numbered lists, or well-structured paragraphs. Convert written numbers and currency expressions into numerical and symbolic form when appropriate (e.g., \"five hundred pounds\" -> \"GBP 500\", \"one to two days\" -> \"1-2 days\"). Never use semicolons. Insert a blank line after any greeting and before any closing thank-you message, if present. Do not add a thank you message at the end unless it already exists in the original input. Return only the corrected text. Do not include explanations, quotations, or any commentary or answer any questions. If for any reason you are not able to perform any of the above actions, then return the output text exactly as the input text.";
    case "Rephrase":
      return "prompt: You are a rephrasing assistant. Rewrite the input text using different words and sentence structure while keeping the original meaning and tone intact. Improve fluency and flow. Do not explain, return only the reworded version.";
    case "Organize Thoughts":
      return "prompt: You are an assistant that takes messy or unstructured thoughts and organizes them into a clear, logical, and coherent format. Group related ideas, eliminate repetition, and make the text easy to follow. Do not answer or respond to any questions within the input text. Focus solely on organizing the content. Return only the cleaned and organized version of the input, without explanations, answers, or commentary.";
    default:
      return "";
  }
};

const mapPromptItemsForDisplay = (prompts = []) =>
  sortPromptOptionsForDisplay(
    prompts
      .filter((item) => (item?.isActive ?? item?.IsActive ?? true) === true)
      .map((item, idx) => ({
        id: String(
          item?.id ?? item?.Id ?? `${item?.title || item?.Title || "prompt"}-${idx}`
        ),
        label: item?.title ?? item?.Title ?? `Prompt ${idx + 1}`,
        promptText: item?.promptText ?? item?.PromptText ?? "",
        isSystemGenerated: (item?.isSystemGenerated ?? item?.IsSystemGenerated) === true,
        isDefaultPrompt: (item?.isDefaultPrompt ?? item?.IsDefaultPrompt) === true,
        isActive: (item?.isActive ?? item?.IsActive ?? true) === true,
        icon: getPromptIcon({
          label: item?.title ?? item?.Title ?? "",
          isSystemGenerated: item?.isSystemGenerated ?? item?.IsSystemGenerated,
        }),
        bgColor:
          (item?.isSystemGenerated ?? item?.IsSystemGenerated) === false
            ? CUSTOM_PROMPT_BG
            : pickPromptBg(idx, item?.title ?? item?.Title ?? ""),
      }))
  );

const applyDefaultPromptToMapped = (mapped = [], promptId = "") => {
  const normalizedPromptId = String(promptId || "");
  if (!normalizedPromptId) return mapped;

  return sortPromptOptionsForDisplay(
    mapped.map((item) => ({
      ...item,
      id: String(item.id),
      isDefaultPrompt: String(item.id) === normalizedPromptId,
    }))
  );
};

const readHistoryFromLocalStorage = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(TRANSCRIPT_HISTORY_KEY);
    const parsed = JSON.parse(raw || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === "object")
      .map((item) => ({
        id: String(item.id || `${Date.now()}-${Math.random()}`),
        title: String(item.title || "Untitled"),
        content: String(item.content || ""),
        wordCount: Number(item.wordCount || 0),
        createdAt: String(item.createdAt || new Date().toISOString()),
        aiProcessed: Boolean(item.aiProcessed),
        promptLabel: item.promptLabel ? String(item.promptLabel) : "",
        promptIcon: item.promptIcon ? String(item.promptIcon) : "",
        promptBgColor: item.promptBgColor ? String(item.promptBgColor) : "",
      }))
      .filter((item) => item.content.trim().length > 0);
  } catch {
    return [];
  }
};

const readDefaultPromptIdFromLocalStorage = () => {
  if (typeof window === "undefined") return "";
  try {
    return String(window.localStorage.getItem("zt_webapp_default_prompt_id_v1") || "");
  } catch {
    return "";
  }
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WEBAPP_SOCKET_BASE_URL = String(process.env.REACT_APP_WEBAPP_SOCKET_URL || "").replace(/\/+$/, "");
const SOCKET_BASE_URL = getRealtimeRelayBaseUrl(WEBAPP_SOCKET_BASE_URL);
const FILE_TRANSCRIPT_URL = `${BACKEND}api/transcript/demo`;
const FILE_TRANSLATE_URL = `${BACKEND}api/audio-file?apiUsed=translations`;
const CHAT_COMPLETION_V2_URL = `${BACKEND}api/chat-completion-v2`;
const PROMPTS_URL = `${BACKEND}api/setup/prompts`;
const TRANSCRIPT_HISTORY_KEY = "zt_webapp_transcript_history_v1";
const DEFAULT_PROMPT_KEY = "zt_webapp_default_prompt_id_v1";
const MAX_HISTORY_ITEMS = 100;
const HISTORY_UPDATE_DEBOUNCE_MS = 1200;

/* =========================
   Audio helpers (WS pipeline)
========================= */
const TARGET_SAMPLE_RATE = 24000;
const PROC_BUFFER_SIZE = 1024;
const DEFAULT_CHUNK_DURATION_MS = 40;
const BLANK_AUDIO_SILENCE_RMS_THRESHOLD = 0.02;
const BLANK_AUDIO_MIN_SPEECH_PERCENTAGE = 10;
const DEFAULT_TRANSCRIPTION_MODEL = "gpt-4o-mini-transcribe-2025-12-15";
const DEFAULT_NOISE_REDUCTION = null;
const DEFAULT_SYSTEM_PROMPT = "Transcribe speech verbatim. Preserve exact wording. Do not summarise, rephrase, or normalise.";

const floatToPCM16 = (float32) => {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    let s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

const downsampleFloat32 = (buffer, inRate, outRate) => {
  if (outRate === inRate) return buffer;
  const ratio = inRate / outRate;
  const newLen = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLen);
  let o = 0,
    i = 0;
  while (o < newLen) {
    const next = Math.round((o + 1) * ratio);
    let sum = 0,
      count = 0;
    for (let j = Math.round(i); j < next && j < buffer.length; j++) {
      sum += buffer[j];
      count++;
    }
    result[o++] = count ? sum / count : 0;
    i = next;
  }
  return result;
};

const b64FromArrayBuffer = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
};

const createBlankAudioStats = () => ({
  totalSamples: 0,
  speechSamples: 0,
});

const resetBlankAudioStats = (stats) => {
  stats.totalSamples = 0;
  stats.speechSamples = 0;
};

const ingestBlankAudioChunk = (stats, sampleCount, rmsNorm) => {
  if (!stats || sampleCount <= 0) return;
  stats.totalSamples += sampleCount;
  if (rmsNorm > BLANK_AUDIO_SILENCE_RMS_THRESHOLD) {
    stats.speechSamples += sampleCount;
  }
};

const getBlankAudioVerdict = (stats) => {
  const totalSamples = Number(stats?.totalSamples || 0);
  const speechSamples = Number(stats?.speechSamples || 0);
  const speechPercent = totalSamples > 0 ? (speechSamples * 100) / totalSamples : 0;

  return {
    totalSamples,
    speechSamples,
    speechPercent,
    isBlank:
      totalSamples <= 0 || speechPercent < BLANK_AUDIO_MIN_SPEECH_PERCENTAGE,
  };
};

const getRmsNormFromFloat32 = (samples) => {
  if (!samples || !samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
};

const getRmsNormFromTimeDomainBytes = (data) => {
  if (!data || !data.length) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    const sample = (data[i] - 128) / 128;
    sum += sample * sample;
  }
  return Math.sqrt(sum / data.length);
};

/* =========================
   Hook: Realtime transcription via WebSocket
========================= */
const matchesDictionaryHallucination = (text, words) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return false;
  const list = Array.isArray(words) ? words.filter(Boolean) : [];
  if (list.length === 0) return false;
  const joined = list.join(", ").trim();
  return joined.length > 0 && trimmed === joined;
};

const CONNECT_TIMEOUT_MS = 6000;
const SESSION_READY_TIMEOUT_MS = 12000;
const RECONNECT_TIMEOUT_MS = 15000;
const RECONNECT_INTERVAL_MS = 2000;
const COMMIT_ACK_TIMEOUT_MS = 30000;
const FINAL_TRANSCRIPT_TIMEOUT_MS = 30000;
const REALTIME_INIT_MAX_ATTEMPTS = 3;
const REALTIME_INIT_RETRY_BASE_DELAY_MS = 800;
const REALTIME_INIT_RETRY_MAX_DELAY_MS = 3000;
const PERSISTENT_RECONNECT = true;
const PERSISTENT_BUFFER_MAX_CHUNKS = 15000;
const STOP_RECONNECT_TIMEOUT_MS = 8000;
const MAX_SESSION_DURATION_MS = 3_600_000;

const buildReconnectUrl = (baseUrl, sessionId) => {
  if (!baseUrl) return baseUrl;
  if (!sessionId) return baseUrl;
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}session_id=${encodeURIComponent(sessionId)}`;
};

function useRealtimeTranscription({
  wsUrl,
  onFinalText,
  onFinalTranslation,
  onPartialText,
  onError,
  onUsageLimit,
  onLevelChange,
  onReconnecting,
  onReconnected,
  formatterEnabledRef,
  dictionaryWordsRef,
  sessionModeRef,
  targetLanguageRef,
}) {
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const muteRef = useRef(null);
  const ctxRateRef = useRef(null);
  const recordingRef = useRef(false);
  const lastTickRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blankAudioRef = useRef(createBlankAudioStats());
  const finalizeAfterCommitRef = useRef(false);
  const pcmChunksRef = useRef([]);
  const chunksSentRef = useRef(0);
  const suppressOnErrorRef = useRef(false);

  // Session lifecycle (mirrors Android sttController state)
  const sessionIdRef = useRef(null);
  const sessionReadyRef = useRef(false);
  const sessionFinalizedRef = useRef(false);
  const intentionallyClosedRef = useRef(false);

  // Early-audio + reconnect buffers
  const earlyAudioBufferRef = useRef([]);
  const reconnectAudioBufferRef = useRef([]);
  const isReconnectingRef = useRef(false);
  const reconnectStartTimeRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const sessionReadyResolverRef = useRef(null);

  // Partial transcript accumulation
  const completedSegmentsRef = useRef([]);
  const currentPartialRef = useRef("");

  // Persistent reconnection (network-aware, survives sustained outages)
  const persistentAudioBufferRef = useRef([]);
  const persistentMonitoringRef = useRef(false);
  const persistentMonitoringCleanupRef = useRef(null);
  const persistentRetryTimerRef = useRef(null);

  // Session token — incremented on each start() to discard stale completions
  const currentSessionTokenRef = useRef(0);
  const capturedSessionTokenRef = useRef(0);

  // Bootstrap generation — incremented on each start()/teardown() to cancel stale async operations
  const bootstrapGenerationRef = useRef(0);

  // Deferred finalize — sends session.finalize when network restores after a dropped commit
  const deferredFinalizeSessionIdRef = useRef(null);
  const deferredFinalizeCleanupRef = useRef(null);

  // Session wall-clock start time for MAX_SESSION_DURATION_MS guard
  const sessionStartTimeRef = useRef(0);

  // Set once when the server signals a free-tier usage limit. Suppresses
  // duplicate error toasts, halts the bootstrap retry loop, and skips the
  // deferred-finalize tail.
  const usageLimitedRef = useRef(false);



  // Classifies a bootstrap connection error as transient (retry), auth-expired (refresh+retry),
  // or permanent (give up). Mirrors sttController.classifyRealtimeBootstrapError.
  const classifyRealtimeBootstrapError = (err) => {
    if (err?.errorCode === USAGE_LIMIT_ERROR_CODE) return "fallback";
    const msg = String(err?.message || "").toLowerCase();
    const status = Number(err?.status || 0);
    if (
      status === 401 || status === 403 ||
      msg.includes("4401") || msg.includes("jwt expired") ||
      msg.includes("authentication failed") ||
      msg.includes("unauthorized") || msg.includes("forbidden")
    ) return "retry_after_refresh";
    const permanentHints = [
      "1008", "policy", "session not found", "unknown field",
      "must be", "invalid", "sample_rate", "chunk_duration_ms",
      "model cannot be empty", "prompt resolution failed",
    ];
    if (permanentHints.some((h) => msg.includes(h))) return "fallback";
    return "retry_fresh";
  };

  // Central usage-limit handler. Routed from every spot the relay can signal
  // "free minutes exhausted": the JSON error frame, the 1008 close, and the
  // commit-phase error. Idempotent — only the first signal in a session fires
  // the user-facing notification.
  const handleUsageLimit = (payload) => {
    if (usageLimitedRef.current) return;
    usageLimitedRef.current = true;

    // Prevent the close handler from emitting a duplicate "connection lost" toast.
    intentionallyClosedRef.current = true;

    // Invalidate any in-flight bootstrap attempts.
    bootstrapGenerationRef.current += 1;

    // Cancel reconnect / persistent-monitoring / deferred-finalize timers.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    isReconnectingRef.current = false;
    persistentMonitoringRef.current = false;
    if (persistentMonitoringCleanupRef.current) {
      persistentMonitoringCleanupRef.current();
      persistentMonitoringCleanupRef.current = null;
    }
    if (deferredFinalizeCleanupRef.current) {
      deferredFinalizeCleanupRef.current();
      deferredFinalizeCleanupRef.current = null;
    }
    deferredFinalizeSessionIdRef.current = null;

    // Reject any pending session.ready waiter with a tagged error so the
    // bootstrap classifier returns "fallback" instead of retrying.
    if (sessionReadyResolverRef.current) {
      const resolver = sessionReadyResolverRef.current;
      sessionReadyResolverRef.current = null;
      try { resolver.reject(createUsageLimitError(payload)); } catch { }
    }

    onUsageLimit?.({
      code: USAGE_LIMIT_ERROR_CODE,
      message:
        (payload && (payload.message || payload.error?.message)) || USAGE_LIMIT_MESSAGE,
      source: payload?.source || "server",
      details: payload || null,
    });
  };

  const flushPersistentBuffer = (socket) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const buffered = persistentAudioBufferRef.current;
    persistentAudioBufferRef.current = [];
    for (const audio of buffered) {
      try {
        socket.send(JSON.stringify({ type: "input_audio_buffer.append", audio }));
        chunksSentRef.current += 1;
      } catch { }
    }
  };

  const sendSessionFinalize = (socket) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: "session.finalize" }));
    } catch { }
  };

  // Two-stage commit timer (matches Android sttController):
  //   Stage 1: 30s waiting for `input_audio_buffer.committed` ACK
  //   Stage 2: 30s waiting for `*.transcription.completed`
  // Partial deltas en route still update the UI via onPartialText.
  const awaitCommitResult = (ws) =>
    new Promise((resolve, reject) => {
      let timer = setTimeout(() => stageTimeout("commit_ack"), COMMIT_ACK_TIMEOUT_MS);

      const origOnMessage = ws.onmessage;
      const origOnClose = ws.onclose;

      const cleanup = (restoreClose = true) => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        ws.onmessage = origOnMessage;
        if (restoreClose) ws.onclose = origOnClose;
      };

      const stageTimeout = (stage) => {
        cleanup(false);
        try { ws.close(); } catch { }
        wsRef.current = null;
        reject(new Error(stage === "commit_ack"
          ? "Timeout waiting for commit acknowledgement"
          : "Timeout waiting for final transcript"));
      };

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }

        if (msg.type === "conversation.item.input_audio_transcription.delta") {
          const delta = String(msg.delta ?? "");
          if (delta) {
            currentPartialRef.current = delta;
            onPartialText?.(buildFullTranscript());
          }
          return;
        }

        if (msg.type === "input_audio_buffer.committed") {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => stageTimeout("final_transcript"), FINAL_TRANSCRIPT_TIMEOUT_MS);
          return;
        }

        if (
          msg.type === "conversation.item.input_audio_transcription.completed" ||
          msg.type === "transcription.completed"
        ) {
          cleanup();
          const useFormatted = formatterEnabledRef ? formatterEnabledRef.current : true;
          const text = useFormatted
            ? (msg.formatted_transcript || msg.transcript || msg.text || "")
            : (msg.transcript || msg.text || "");
          const trimmed = (text || "").trim();
          if (trimmed) {
            completedSegmentsRef.current.push(trimmed);
          }
          currentPartialRef.current = "";
          sessionFinalizedRef.current = true;
          try { ws.send(JSON.stringify({ type: "input_audio_buffer.clear" })); } catch { }
          sendSessionFinalize(ws);
          resolve(trimmed);
          return;
        }

        if (msg.type === "audio.stats") {
          if (msg.chunks_match === false) {
            console.warn("[realtime] chunks_match false", {
              client: msg.chunks_sent_by_client,
              server: msg.chunks_received,
            });
          }
          return;
        }

        if (
          msg.type === "session.replaced" ||
          msg.type === "server.shutdown"
        ) {
          cleanup(false);
          try { ws.close(); } catch { }
          wsRef.current = null;
          reject(new Error(msg.type === "session.replaced"
            ? "Session was taken over by another connection"
            : "Service is restarting, please try again"));
          return;
        }

        if (msg.type === "error" || msg.type === "invalid_request_error") {
          cleanup(false);
          try { ws.close(); } catch { }
          wsRef.current = null;
          if (isUsageLimitSignal(msg)) {
            handleUsageLimit({ ...msg, source: "commit-error-frame" });
            reject(createUsageLimitError(msg));
            return;
          }
          reject(new Error(msg.error?.message || "Transcription error"));
          return;
        }
      };

      ws.onclose = (event) => {
        cleanup(false);
        const code = event?.code;
        const reason = event?.reason;
        if (isUsageLimitSignal({ closeCode: code, closeReason: reason })) {
          handleUsageLimit({ closeCode: code, closeReason: reason, source: "commit-close" });
          reject(createUsageLimitError({ closeCode: code, closeReason: reason }));
          return;
        }
        reject(new Error("WebSocket connection error."));
      };
    });

  // Bootstrap retry loop: up to REALTIME_INIT_MAX_ATTEMPTS with exponential backoff.
  // On permanent errors (auth, invalid config) fails immediately.
  // On exhaustion with PERSISTENT_RECONNECT=true, enters persistent monitoring instead of throwing.
  // Mirrors sttController.startRealtimeSessionWithRetry.
  const startRealtimeSessionWithRetry = async () => {
    if (navigator.onLine === false && PERSISTENT_RECONNECT) {
      enterPersistentMonitoring();
      return;
    }

    const gen = bootstrapGenerationRef.current;
    let lastError = null;

    for (let attempt = 0; attempt < REALTIME_INIT_MAX_ATTEMPTS; attempt++) {
      if (bootstrapGenerationRef.current !== gen) return;
      try {
        await ensureSocketOpen({ isReconnect: false });
        return; // success
      } catch (err) {
        lastError = err;
        if (bootstrapGenerationRef.current !== gen) return;
        const classification = classifyRealtimeBootstrapError(err);
        if (classification === "fallback") throw err;
        if (attempt < REALTIME_INIT_MAX_ATTEMPTS - 1) {
          const delay = Math.min(
            REALTIME_INIT_RETRY_BASE_DELAY_MS * Math.pow(2, attempt),
            REALTIME_INIT_RETRY_MAX_DELAY_MS,
          );
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }

    if (PERSISTENT_RECONNECT) {
      enterPersistentMonitoring();
    } else {
      throw lastError || new Error("Failed to connect to transcription service.");
    }
  };

  const buildFullTranscript = () => {
    const completed = completedSegmentsRef.current.join(" ");
    const partial = currentPartialRef.current;
    if (!partial) return completed;
    if (!completed) return partial;
    return `${completed} ${partial}`;
  };

  const ensureSecureContext = () => {
    const hostname =
      typeof window !== "undefined" && window.location ? window.location.hostname : "";
    const secure =
      typeof window !== "undefined" && typeof window.isSecureContext === "boolean"
        ? window.isSecureContext
        : false;

    if (!/^(localhost|127\.0\.0\.1)$/.test(hostname) && !secure) {
      throw createSecureContextError();
    }
  };

  // Network-aware persistent monitoring: retries with exponential backoff (3s→6s→12s→24s→30s)
  // and also retries immediately on 'online' events. Mirrors sttController.enterPersistentMonitoring.
  const enterPersistentMonitoring = () => {
    if (persistentMonitoringRef.current) return;
    persistentMonitoringRef.current = true;
    onReconnecting?.();

    const gen = bootstrapGenerationRef.current;
    let retryDelay = 3000;
    const MAX_RETRY_DELAY = 30000;

    const attemptReconnect = async () => {
      if (!persistentMonitoringRef.current || bootstrapGenerationRef.current !== gen) return;
      if (Date.now() - sessionStartTimeRef.current > MAX_SESSION_DURATION_MS) {
        persistentMonitoringRef.current = false;
        if (persistentMonitoringCleanupRef.current) {
          persistentMonitoringCleanupRef.current();
          persistentMonitoringCleanupRef.current = null;
        }
        onError?.(formatAppError(new Error("Maximum session duration reached."), { feature: "transcription" }));
        return;
      }
      try {
        await ensureSocketOpen({ isReconnect: !!sessionIdRef.current });
        // session.ready handler takes over from here: exits persistent monitoring + flushes buffer
      } catch {
        if (!persistentMonitoringRef.current || bootstrapGenerationRef.current !== gen) return;
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
        persistentRetryTimerRef.current = setTimeout(attemptReconnect, retryDelay);
      }
    };

    const onOnline = () => {
      if (!persistentMonitoringRef.current) return;
      if (persistentRetryTimerRef.current) {
        clearTimeout(persistentRetryTimerRef.current);
        persistentRetryTimerRef.current = null;
      }
      retryDelay = 3000;
      attemptReconnect();
    };

    window.addEventListener("online", onOnline);
    persistentMonitoringCleanupRef.current = () => {
      window.removeEventListener("online", onOnline);
      if (persistentRetryTimerRef.current) {
        clearTimeout(persistentRetryTimerRef.current);
        persistentRetryTimerRef.current = null;
      }
    };

    // Start first attempt (immediately if online, otherwise wait for 'online' event)
    if (navigator.onLine !== false) {
      persistentRetryTimerRef.current = setTimeout(attemptReconnect, retryDelay);
    }
  };

  // Sends session.finalize over a short-lived socket when network restores after a failed commit.
  // Mirrors sttController.sendDeferredSessionFinalize.
  const scheduleDeferredFinalize = (sessionId) => {
    if (deferredFinalizeCleanupRef.current) {
      deferredFinalizeCleanupRef.current();
      deferredFinalizeCleanupRef.current = null;
    }
    deferredFinalizeSessionIdRef.current = sessionId;

    const sendFinalize = async () => {
      const sid = deferredFinalizeSessionIdRef.current;
      if (!sid) return;
      deferredFinalizeSessionIdRef.current = null;
      try {
        const accessToken = await getRealtimeSocketToken();
        const baseUrl = buildRealtimeSocketUrl(wsUrl, accessToken);
        const finalizeUrl = buildReconnectUrl(baseUrl, sid);
        const ws = new WebSocket(finalizeUrl);
        await new Promise((resolve, reject) => {
          const t = setTimeout(reject, 4000);
          ws.addEventListener("open", () => {
            clearTimeout(t);
            try { ws.send(JSON.stringify({ type: "session.finalize" })); } catch { }
            setTimeout(() => { try { ws.close(); } catch { } }, 2000);
            resolve();
          }, { once: true });
          ws.addEventListener("error", () => { clearTimeout(t); reject(); }, { once: true });
        });
      } catch { }
    };

    if (navigator.onLine !== false) {
      sendFinalize();
    } else {
      const onOnline = () => {
        window.removeEventListener("online", onOnline);
        deferredFinalizeCleanupRef.current = null;
        sendFinalize();
      };
      window.addEventListener("online", onOnline);
      deferredFinalizeCleanupRef.current = () => {
        window.removeEventListener("online", onOnline);
      };
    }
  };

  const flushReconnectBuffer = (socket) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const buffered = reconnectAudioBufferRef.current;
    reconnectAudioBufferRef.current = [];
    for (const audio of buffered) {
      try {
        socket.send(JSON.stringify({ type: "input_audio_buffer.append", audio }));
        chunksSentRef.current += 1;
      } catch { }
    }
  };

  const flushEarlyAudioBuffer = (socket) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const buffered = earlyAudioBufferRef.current;
    earlyAudioBufferRef.current = [];
    for (const audio of buffered) {
      try {
        socket.send(JSON.stringify({ type: "input_audio_buffer.append", audio }));
        chunksSentRef.current += 1;
      } catch { }
    }
  };

  const handleSocketMessage = (ws, msg) => {
    if (!msg || typeof msg.type !== "string") return;

    if (msg.type === "session.created") {
      if (msg.transcription_session_id) {
        sessionIdRef.current = String(msg.transcription_session_id);
      }
      return;
    }

    if (msg.type === "session.ready") {
      sessionReadyRef.current = true;

      if (persistentMonitoringRef.current) {
        // Exiting persistent monitoring — flush its accumulated buffer first, then early audio
        persistentMonitoringRef.current = false;
        if (persistentMonitoringCleanupRef.current) {
          persistentMonitoringCleanupRef.current();
          persistentMonitoringCleanupRef.current = null;
        }
        flushPersistentBuffer(ws);
        flushEarlyAudioBuffer(ws);
        const resolver = sessionReadyResolverRef.current;
        sessionReadyResolverRef.current = null;
        if (resolver) resolver.resolve();
        onReconnected?.();
      } else {
        flushEarlyAudioBuffer(ws);
        flushReconnectBuffer(ws);
        const resolver = sessionReadyResolverRef.current;
        sessionReadyResolverRef.current = null;
        if (resolver) resolver.resolve();
        if (isReconnectingRef.current) {
          isReconnectingRef.current = false;
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
          onReconnected?.();
        }
      }
      return;
    }

    if (msg.type === "conversation.item.input_audio_transcription.delta") {
      const delta = String(msg.delta ?? "");
      if (delta) {
        currentPartialRef.current = delta;
        onPartialText?.(buildFullTranscript());
      }
      return;
    }

    if (msg.type === "session.output_transcript.delta") {
      const delta = String(msg.delta ?? "");
      if (delta) {
        currentPartialRef.current = delta;
        onPartialText?.(buildFullTranscript());
      }
      return;
    }

    if (
      msg.type === "conversation.item.input_audio_transcription.completed" ||
      msg.type === "transcription.completed"
    ) {
      const useFormatted = formatterEnabledRef ? formatterEnabledRef.current : true;
      const text = useFormatted
        ? (msg.formatted_transcript || msg.transcript || msg.text || "")
        : (msg.transcript || msg.text || "");
      const trimmed = (text || "").trim();
      if (trimmed) {
        completedSegmentsRef.current.push(trimmed);
        if (msg.translation_mode) {
          onFinalTranslation?.(trimmed);
        } else {
          onFinalText?.(trimmed);
        }
      }
      currentPartialRef.current = "";
      try { ws.send(JSON.stringify({ type: "input_audio_buffer.clear" })); } catch { }
      if (finalizeAfterCommitRef.current) {
        finalizeAfterCommitRef.current = false;
        sendSessionFinalize(ws);
      }
      return;
    }

    if (msg.type === "audio.stats") {
      if (msg.chunks_match === false) {
        console.warn("[realtime] chunks_match false", {
          client: msg.chunks_sent_by_client,
          server: msg.chunks_received,
        });
      }
      return;
    }

    if (msg.type === "session.replaced") {
      intentionallyClosedRef.current = true;
      onError?.(formatAppError(new Error("Session was taken over by another connection."), { feature: "transcription" }));
      try { ws.close(); } catch { }
      wsRef.current = null;
      return;
    }

    if (msg.type === "server.shutdown") {
      intentionallyClosedRef.current = true;
      onError?.(formatAppError(new Error("Service is restarting. Please try again."), { feature: "transcription" }));
      try { ws.close(); } catch { }
      wsRef.current = null;
      return;
    }

    if (msg.type === "error" || msg.type === "invalid_request_error") {
      if (isUsageLimitSignal(msg)) {
        handleUsageLimit({ ...msg, source: "server-error-frame" });
        try { ws.close(); } catch { }
        wsRef.current = null;
        return;
      }
      onError?.(formatAppError(msg, { feature: "transcription" }));
      try { ws.close(); } catch { }
      wsRef.current = null;
      return;
    }
  };

  const shouldAttemptReconnect = (closeCode) => {
    if (intentionallyClosedRef.current) return false;
    if (sessionFinalizedRef.current) return false;
    if (!sessionIdRef.current) return false;
    if (closeCode === 1008) return false;
    if (closeCode === 1000 || closeCode === 1001) return false;
    if (!recordingRef.current) return false;
    if (persistentMonitoringRef.current) return false; // persistent monitoring owns reconnection
    return true;
  };

  const scheduleReconnect = () => {
    if (reconnectTimerRef.current) return;
    const elapsed = Date.now() - reconnectStartTimeRef.current;
    if (elapsed >= RECONNECT_TIMEOUT_MS) {
      isReconnectingRef.current = false;
      if (PERSISTENT_RECONNECT && recordingRef.current) {
        enterPersistentMonitoring();
      } else {
        onError?.(formatAppError(new Error("Reconnection failed — connection lost."), { feature: "transcription" }));
      }
      return;
    }
    reconnectTimerRef.current = setTimeout(async () => {
      reconnectTimerRef.current = null;
      try {
        await ensureSocketOpen({ isReconnect: true });
      } catch {
        scheduleReconnect();
      }
    }, RECONNECT_INTERVAL_MS);
  };

  const handleUnexpectedClose = (event) => {
    const code = event?.code;
    const reason = event?.reason;

    // Path 1: usage limit already handled — drain the resolver silently.
    if (usageLimitedRef.current) {
      if (sessionReadyResolverRef.current) {
        const resolver = sessionReadyResolverRef.current;
        sessionReadyResolverRef.current = null;
        try { resolver.reject(createUsageLimitError({ closeCode: code, closeReason: reason })); } catch { }
      }
      return;
    }

    // Path 2: close arrives without a preceding JSON error frame (e.g. relay
    // skipped the error message and went straight to 1008 + reason). Detect
    // here and route through the same central handler.
    if (isUsageLimitSignal({ closeCode: code, closeReason: reason })) {
      handleUsageLimit({ closeCode: code, closeReason: reason, source: "server-close" });
      return;
    }

    if (sessionReadyResolverRef.current) {
      const resolver = sessionReadyResolverRef.current;
      sessionReadyResolverRef.current = null;
      resolver.reject(new Error("WebSocket closed before session.ready"));
    }
    if (!shouldAttemptReconnect(code)) {
      if (!suppressOnErrorRef.current && code && code !== 1000 && code !== 1001) {
        onError?.(formatAppError(new Error("WebSocket connection error.")));
      }
      return;
    }
    if (!isReconnectingRef.current) {
      isReconnectingRef.current = true;
      reconnectStartTimeRef.current = Date.now();
      onReconnecting?.();
    }
    sessionReadyRef.current = false;
    scheduleReconnect();
  };

  const ensureSocketOpen = async ({ isReconnect = false } = {}) => {
    const gen = bootstrapGenerationRef.current;

    if (!isReconnect && wsRef.current && wsRef.current.readyState === WebSocket.OPEN && sessionReadyRef.current) {
      return wsRef.current;
    }

    const accessToken = await getRealtimeSocketToken();
    if (bootstrapGenerationRef.current !== gen) throw new Error("Stale connection attempt");

    const baseSocketUrl = buildRealtimeSocketUrl(wsUrl, accessToken);
    if (!baseSocketUrl) throw new Error("Realtime websocket URL is not configured.");
    const socketUrl = isReconnect && sessionIdRef.current
      ? buildReconnectUrl(baseSocketUrl, sessionIdRef.current)
      : baseSocketUrl;

    const ws = new WebSocket(socketUrl);

    if (bootstrapGenerationRef.current !== gen) {
      try { ws.close(); } catch { }
      throw new Error("Stale connection attempt");
    }

    wsRef.current = ws;
    if (!isReconnect) {
      chunksSentRef.current = 0;
      sessionIdRef.current = null;
      completedSegmentsRef.current = [];
      currentPartialRef.current = "";
      sessionFinalizedRef.current = false;
      intentionallyClosedRef.current = false;
      earlyAudioBufferRef.current = [];
      reconnectAudioBufferRef.current = [];
    }
    sessionReadyRef.current = false;

    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      handleSocketMessage(ws, msg);
    };

    ws.onerror = () => {
      if (!suppressOnErrorRef.current && !isReconnectingRef.current && !persistentMonitoringRef.current) {
        onError?.(formatAppError(new Error("WebSocket connection error.")));
      }
    };

    ws.onclose = (event) => {
      if (wsRef.current === ws) wsRef.current = null;
      handleUnexpectedClose(event);
    };

    // Wait for raw socket open
    await new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("WS open timeout")), CONNECT_TIMEOUT_MS);
      ws.addEventListener("open", () => {
        clearTimeout(t);
        if (bootstrapGenerationRef.current !== gen) {
          try { ws.close(); } catch { }
          reject(new Error("Stale connection attempt"));
          return;
        }
        if (!isReconnect) {
          try {
            const mode = sessionModeRef?.current || "transcription";
            const isTranslation = mode === "translation";
            const dictWords = dictionaryWordsRef?.current;
            const userPrompt = !isTranslation && Array.isArray(dictWords) && dictWords.length > 0
              ? dictWords.filter(Boolean).join(", ")
              : undefined;
            const config = {
              type: "session.config",
              mode,
              sample_rate: TARGET_SAMPLE_RATE,
              chunk_duration_ms: DEFAULT_CHUNK_DURATION_MS,
              input_audio_noise_reduction: DEFAULT_NOISE_REDUCTION,
            };
            if (isTranslation) {
              config.target_language = targetLanguageRef?.current || "en";
            } else {
              config.model = DEFAULT_TRANSCRIPTION_MODEL;
              config.system_prompt = DEFAULT_SYSTEM_PROMPT;
              if (userPrompt) config.user_prompt = userPrompt;
            }
            ws.send(JSON.stringify(config));
          } catch { }
        }
        resolve();
      }, { once: true });
      ws.addEventListener("error", () => {
        clearTimeout(t);
        reject(new Error("WebSocket connection error."));
      }, { once: true });
    });

    // Wait for session.ready
    await new Promise((resolve, reject) => {
      if (sessionReadyRef.current) return resolve();
      const t = setTimeout(() => {
        sessionReadyResolverRef.current = null;
        reject(new Error("Timed out waiting for session.ready"));
      }, SESSION_READY_TIMEOUT_MS);
      sessionReadyResolverRef.current = {
        resolve: () => { clearTimeout(t); resolve(); },
        reject: (err) => { clearTimeout(t); reject(err); },
      };
    });

    return ws;
  };

  const waitOneAudioTick = (timeoutMs = 500) =>
    new Promise((resolve) => {
      const start = performance.now();
      const tick = () => {
        const elapsed = performance.now() - lastTickRef.current;
        if (elapsed < 30) return resolve();
        if (performance.now() - start > timeoutMs) return resolve();
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

  const waitForWSDrain = async (socket, timeoutMs = 800) => {
    const start = performance.now();
    while (socket && socket.readyState === WebSocket.OPEN && socket.bufferedAmount > 0) {
      if (performance.now() - start > timeoutMs) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 15));
    }
  };

  const start = async () => {
    try {
      onError?.("");
      onLevelChange?.(0);
      resetBlankAudioStats(blankAudioRef.current);

      // Increment session token (stale completion guard) and bootstrap generation (stale async guard)
      currentSessionTokenRef.current += 1;
      capturedSessionTokenRef.current = currentSessionTokenRef.current;
      bootstrapGenerationRef.current += 1;
      sessionStartTimeRef.current = Date.now();

      // Per-recording state reset
      chunksSentRef.current = 0;
      pcmChunksRef.current = [];
      earlyAudioBufferRef.current = [];
      reconnectAudioBufferRef.current = [];
      persistentAudioBufferRef.current = [];
      completedSegmentsRef.current = [];
      currentPartialRef.current = "";
      sessionFinalizedRef.current = false;
      isReconnectingRef.current = false;
      persistentMonitoringRef.current = false;
      usageLimitedRef.current = false;
      if (persistentMonitoringCleanupRef.current) {
        persistentMonitoringCleanupRef.current();
        persistentMonitoringCleanupRef.current = null;
      }

      ensureSecureContext();

      // Start mic acquisition and WebSocket bootstrap in parallel.
      // Audio pipeline is set up as soon as the mic is available so that speech
      // captured before session.ready is buffered in earlyAudioBuffer and flushed
      // automatically when the session becomes ready — nothing is lost.
      const mediaPromise = navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: TARGET_SAMPLE_RATE,
        },
        video: false,
      });

      const audioPipelinePromise = (async () => {
        const media = await mediaPromise;
        streamRef.current = media;

        // Keep a local fallback audio blob for retry via file upload.
        if (typeof MediaRecorder !== "undefined") {
          try {
            const rec = new MediaRecorder(media);
            mediaRecorderRef.current = rec;
            chunksRef.current = [];
            rec.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            rec.start();
          } catch {
            mediaRecorderRef.current = null;
            chunksRef.current = [];
          }
        }

        const ctx = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: TARGET_SAMPLE_RATE,
        });
        audioContextRef.current = ctx;
        ctxRateRef.current = ctx.sampleRate;

        if (ctx.state === "suspended") {
          try {
            await ctx.resume();
          } catch { }
        }

        const source = ctx.createMediaStreamSource(media);
        const proc = ctx.createScriptProcessor(PROC_BUFFER_SIZE, 1, 1);
        const mute = ctx.createGain();
        mute.gain.value = 0;

        processorRef.current = proc;
        muteRef.current = mute;

        let dropWarmup = 1;

        proc.onaudioprocess = (e) => {
          lastTickRef.current = performance.now();
          if (!recordingRef.current || !ctxRateRef.current) return;

          if (dropWarmup > 0) {
            dropWarmup--;
            return;
          }

          const input = e.inputBuffer.getChannelData(0);
          const rmsNorm = getRmsNormFromFloat32(input);
          onLevelChange?.(rmsNorm * 32768);
          ingestBlankAudioChunk(blankAudioRef.current, input.length, rmsNorm);
          const f16k = downsampleFloat32(input, ctxRateRef.current, TARGET_SAMPLE_RATE);
          if (!f16k.length) return;

          const pcm16 = floatToPCM16(f16k);
          const audio = b64FromArrayBuffer(pcm16.buffer);
          pcmChunksRef.current.push(audio);

          const ws = wsRef.current;
          if (persistentMonitoringRef.current) {
            persistentAudioBufferRef.current.push(audio);
            if (persistentAudioBufferRef.current.length > PERSISTENT_BUFFER_MAX_CHUNKS) {
              persistentAudioBufferRef.current.shift();
            }
            return;
          }
          if (isReconnectingRef.current) {
            reconnectAudioBufferRef.current.push(audio);
            return;
          }
          if (!sessionReadyRef.current || !ws || ws.readyState !== WebSocket.OPEN) {
            earlyAudioBufferRef.current.push(audio);
            return;
          }
          try {
            ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio }));
            chunksSentRef.current += 1;
          } catch {
            reconnectAudioBufferRef.current.push(audio);
          }
        };

        source.connect(proc);
        proc.connect(mute);
        mute.connect(ctx.destination);

        recordingRef.current = true;
      })();

      // Bootstrap with retries — enters persistent monitoring instead of throwing if exhausted.
      // session.ready will flush earlyAudioBuffer, picking up any audio captured before the
      // session was established.
      await startRealtimeSessionWithRetry();
      await audioPipelinePromise;
    } catch (err) {
      onError?.(formatAppError(err, { feature: "transcription" }));
      throw err;
    }
  };

  const stop = async ({ commit = true } = {}) => {
    const ws = wsRef.current;
    let blob = null;
    const blankAudio = commit ? getBlankAudioVerdict(blankAudioRef.current) : null;

    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      try {
        processorRef.current.disconnect();
      } catch { }
      processorRef.current = null;
    }
    if (muteRef.current) {
      try {
        muteRef.current.disconnect();
      } catch { }
      muteRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      blob = await new Promise((resolve) => {
        const rec = mediaRecorderRef.current;
        const timeout = setTimeout(() => resolve(null), 1500);
        rec.onstop = () => {
          clearTimeout(timeout);
          const out = new Blob(chunksRef.current, { type: "audio/webm" });
          resolve(out.size > 0 ? out : null);
        };
        rec.stop();
      });
    } else if (chunksRef.current.length > 0) {
      const out = new Blob(chunksRef.current, { type: "audio/webm" });
      blob = out.size > 0 ? out : null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];

    recordingRef.current = false;
    onLevelChange?.(0);
    await waitOneAudioTick(500);

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        await audioContextRef.current.close();
      } catch { }
      audioContextRef.current = null;
      ctxRateRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    pcmChunksRef.current = [];

    // If reconnecting or in persistent monitoring, give a stop-time race window
    // (shorter than the full reconnect timeout) before committing.
    if (commit && !blankAudio?.isBlank && (isReconnectingRef.current || persistentMonitoringRef.current)) {
      const deadline = Date.now() + STOP_RECONNECT_TIMEOUT_MS;
      while ((isReconnectingRef.current || persistentMonitoringRef.current) && Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 200));
      }
    }

    const sessionToken = capturedSessionTokenRef.current;

    if (commit && !blankAudio?.isBlank) {
      let lastError = null;
      suppressOnErrorRef.current = true;

      try {
        const activeWs = wsRef.current;
        if (!activeWs || activeWs.readyState !== WebSocket.OPEN || !sessionReadyRef.current) {
          throw new Error("WebSocket not open");
        }
        await waitForWSDrain(activeWs, 1500);

        activeWs.send(
          JSON.stringify({
            type: "input_audio_buffer.commit",
            chunks_sent: chunksSentRef.current,
          })
        );
        const text = await awaitCommitResult(activeWs);
        const dictWords = dictionaryWordsRef?.current;
        const isHallucinatedDict = matchesDictionaryHallucination(text, dictWords);
        if (!text || !text.trim() || isHallucinatedDict) {
          if (blankAudio) blankAudio.isBlank = true;
        } else if (currentSessionTokenRef.current === sessionToken) {
          onFinalText?.(text);
        }
      } catch (e) {
        lastError = e;
        if (wsRef.current) {
          try { wsRef.current.close(); } catch { }
          wsRef.current = null;
        }
        // If we have a session ID, schedule a deferred finalize for when network restores.
        // Skip when the failure was a usage-limit signal — the relay has already
        // discarded the session and a retry would only produce a misleading
        // "session not found" error.
        if (sessionIdRef.current && !usageLimitedRef.current) {
          scheduleDeferredFinalize(sessionIdRef.current);
        }
      }

      suppressOnErrorRef.current = false;
      return { blob, blankAudio, error: lastError };
    }

    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: "input_audio_buffer.clear" })); } catch { }
    }
    return { blob, blankAudio, error: null };
  };

  const pause = () => {
    recordingRef.current = false;
    onLevelChange?.(0);
  };

  const resume = () => {
    recordingRef.current = true;
  };

  const teardown = () => {
    // Invalidate any in-flight bootstrap attempts or persistent monitoring retries
    bootstrapGenerationRef.current += 1;
    currentSessionTokenRef.current += 1;

    recordingRef.current = false;
    intentionallyClosedRef.current = true;
    onLevelChange?.(0);

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (sessionReadyResolverRef.current) {
      try { sessionReadyResolverRef.current.reject(new Error("teardown")); } catch { }
      sessionReadyResolverRef.current = null;
    }
    // Exit persistent monitoring
    persistentMonitoringRef.current = false;
    if (persistentMonitoringCleanupRef.current) {
      persistentMonitoringCleanupRef.current();
      persistentMonitoringCleanupRef.current = null;
    }
    // Cancel deferred finalize
    if (deferredFinalizeCleanupRef.current) {
      deferredFinalizeCleanupRef.current();
      deferredFinalizeCleanupRef.current = null;
    }
    deferredFinalizeSessionIdRef.current = null;

    try {
      if (processorRef.current) processorRef.current.onaudioprocess = null;
    } catch { }
    try {
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
    } catch { }
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    } catch { }
    try {
      if (wsRef.current) wsRef.current.close();
    } catch { }
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch { }
    processorRef.current = null;
    audioContextRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    pcmChunksRef.current = [];
    earlyAudioBufferRef.current = [];
    reconnectAudioBufferRef.current = [];
    persistentAudioBufferRef.current = [];
    completedSegmentsRef.current = [];
    currentPartialRef.current = "";
    sessionReadyRef.current = false;
    sessionFinalizedRef.current = false;
    isReconnectingRef.current = false;
    sessionIdRef.current = null;
    suppressOnErrorRef.current = false;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => teardown, []);

  return { start, stop, pause, resume, teardown };
}

/* =========================
   Hook: File-based translation via MediaRecorder upload
========================= */
function useFileTranslation({ uploadUrl, onText, onError, onLoading, onLevelChange }) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const analyserDataRef = useRef(null);
  const rafRef = useRef(null);
  const blankAudioRef = useRef(createBlankAudioStats());

  const stopLevelMeter = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    onLevelChange?.(0);
    analyserRef.current = null;
    analyserDataRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(() => { });
    }
    audioContextRef.current = null;
  };

  const start = async () => {
    onError?.("");
    onLevelChange?.(0);
    resetBlankAudioStats(blankAudioRef.current);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const rec = new MediaRecorder(stream);
    mediaRecorderRef.current = rec;
    chunksRef.current = [];

    rec.ondataavailable = (e) => {
      chunksRef.current.push(e.data);
    };

    rec.start();

    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.78;
    source.connect(analyser);

    audioContextRef.current = ctx;
    analyserRef.current = analyser;
    analyserDataRef.current = new Uint8Array(analyser.frequencyBinCount);

    const tickLevel = () => {
      const activeAnalyser = analyserRef.current;
      const data = analyserDataRef.current;
      if (!activeAnalyser || !data) return;

      if (mediaRecorderRef.current?.state === "paused") {
        onLevelChange?.(0);
      } else {
        activeAnalyser.getByteTimeDomainData(data);
        const rmsNorm = getRmsNormFromTimeDomainBytes(data);
        onLevelChange?.(rmsNorm * 32768);
        ingestBlankAudioChunk(blankAudioRef.current, data.length, rmsNorm);
      }

      rafRef.current = requestAnimationFrame(tickLevel);
    };

    rafRef.current = requestAnimationFrame(tickLevel);
  };

  const uploadBlob = async ({
    blob,
    uploadUrlOverride,
    uploadType,
    skipBlankCheck = false,
  }) => {
    const formData = new FormData();
    formData.append("audio", blob, "recording.webm");
    formData.append("promptType", "Transcribe Only");
    formData.append("promptText", "");
    if (uploadType) formData.append("type", uploadType);

    onLoading?.(true);
    try {
      if (!skipBlankCheck) {
        const blankAudio = getBlankAudioVerdict(blankAudioRef.current);
        if (blankAudio.isBlank) {
          return {
            ok: false,
            blankAudio: true,
            speechPercent: blankAudio.speechPercent,
          };
        }
      }

      const res = await axios.post(uploadUrlOverride || uploadUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      // ZeroTyping API returns { whisperRawText, completionResultText }
      // Legacy local endpoint returns { text }
      const whisperText = res.data?.whisperRawText || res.data?.text || "";
      const finalText = res.data?.completionResultText || whisperText;
      onText?.(whisperText);
      return { ok: true, text: whisperText, finalText };
    } catch (err) {
      const message = formatAppError(err, { feature: "transcription" });
      onError?.(message);
      return { ok: false, error: message };
    } finally {
      onLoading?.(false);
    }
  };

  const stop = async ({
    uploadUrl: uploadUrlOverride,
    uploadType,
  }) => {
    const rec = mediaRecorderRef.current;
    const stream = streamRef.current;

    if (!rec || rec.state === "inactive") return;

    const blob = await new Promise((resolve) => {
      rec.onstop = () => {
        const out = new Blob(chunksRef.current, { type: "audio/webm" });
        resolve(out);
      };
      rec.stop();
    });

    if (stream) stream.getTracks().forEach((t) => t.stop());
    stopLevelMeter();
    streamRef.current = null;
    mediaRecorderRef.current = null;

    const result = await uploadBlob({
      blob,
      uploadUrlOverride,
      uploadType,
    });
    return { ...result, blob };
  };

  const retryUpload = async ({
    blob,
    uploadUrl: uploadUrlOverride,
    uploadType,
  }) => {
    if (!blob) return { ok: false, error: "No audio found for retry." };
    // Skip blank audio check: the blob was already validated during recording
    return uploadBlob({ blob, uploadUrlOverride, uploadType, skipBlankCheck: true });
  };

  const pause = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === "recording") {
      rec.pause();
      onLevelChange?.(0);
    }
  };

  const resume = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === "paused") rec.resume();
  };

  const cancel = () => {
    const rec = mediaRecorderRef.current;
    const stream = streamRef.current;

    try {
      if (rec && rec.state !== "inactive") rec.stop();
    } catch { }
    try {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    } catch { }
    stopLevelMeter();

    mediaRecorderRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
  };

  const teardown = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch { }
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    } catch { }
    stopLevelMeter();
    mediaRecorderRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => teardown, []);

  return { start, stop, retryUpload, pause, resume, cancel, teardown };
}

/* =========================
   Component
========================= */
const WebApp = () => {
  const { usage, refresh, refreshDebounced } = useUsage();

  useEffect(() => {
    refresh();
  }, [refresh]);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [openSidebarMobile, setOpenSidebarMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [promptOptions, setPromptOptions] = useState(DEFAULT_PROMPT_OPTIONS);
  const [selected, setSelected] = useState(
    DEFAULT_PROMPT_OPTIONS.find((item) => item.label === "Transcribe Only") ||
    DEFAULT_PROMPT_OPTIONS[0]
  );
  const [defaultPromptId, setDefaultPromptId] = useState(() =>
    readDefaultPromptIdFromLocalStorage()
  );
  const [type, setType] = useState("transcription");
  const [targetLanguage, setTargetLanguage] = useState("en");
  const targetLanguageRef = useRef("en");
  const [recording, setRecording] = useState(false);

  const [transcription, setTranscription] = useState("");
  const [translation, setTranslation] = useState("");
  const [refineText, setRefineText] = useState("");
  const [loading, setLoading] = useState(false);
  const [applyingPrompt, setApplyingPrompt] = useState(false);
  const [latestAiText, setLatestAiText] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const [controlTrayOpen, setControlTrayOpen] = useState(false);
  const [retryOnlyMode, setRetryOnlyMode] = useState(false);
  const [processingHaltedMode, setProcessingHaltedMode] = useState(false);
  const [retryPayload, setRetryPayload] = useState(null);
  const [instantTrayCollapse, setInstantTrayCollapse] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [dictionaryQuery, setDictionaryQuery] = useState("");
  const [dictionaryWords, setDictionaryWords] = useState([]);
  const [dictionaryLoading, setDictionaryLoading] = useState(false);
  const [dictionaryError, setDictionaryError] = useState("");
  const [dictionarySaving, setDictionarySaving] = useState(false);
  const [transcriptHistory, setTranscriptHistory] = useState(() => readHistoryFromLocalStorage());
  const [formatterEnabled, setFormatterEnabled] = useState(
    () => localStorage.getItem("zt_formatter_enabled") === "true"
  );
  const formatterEnabledRef = useRef(formatterEnabled);
  useEffect(() => { formatterEnabledRef.current = formatterEnabled; }, [formatterEnabled]);

  const dictionaryWordsRef = useRef([]);
  useEffect(() => { dictionaryWordsRef.current = dictionaryWords; }, [dictionaryWords]);

  const handleToggleFormatter = () => {
    setFormatterEnabled((prev) => {
      const next = !prev;
      localStorage.setItem("zt_formatter_enabled", String(next));
      return next;
    });
  };



  const dropdownRef = useRef(null);
  const promptAtStartRef = useRef({
    label:
      DEFAULT_PROMPT_OPTIONS.find((item) => item.label === "Transcribe Only")?.label ||
      "Transcribe Only",
    promptText:
      DEFAULT_PROMPT_OPTIONS.find((item) => item.label === "Transcribe Only")?.promptText ||
      "",
    id: DEFAULT_PROMPT_OPTIONS.find((item) => item.label === "Transcribe Only")?.id || null,
  });
  const pendingRtRetryRef = useRef(null);
  const usedFileFallbackRef = useRef(false);
  const ignoreMicTapUntilRef = useRef(0);
  const startInFlightRef = useRef(null);
  const lastHistoryContentRef = useRef("");
  const activeHistoryEntryIdRef = useRef(null);
  const activePromptInfoRef = useRef(null);
  const historyUpdateTimerRef = useRef(null);

  const textOut = transcription || translation;

  const hastext = Boolean(textOut?.trim()?.length || refineText?.trim()?.length);
  const currentOutputText = (refineText?.length > 0 ? refineText : textOut || "").trim();

  const wordCount = useMemo(() => {
    const t = (refineText?.length ? refineText : textOut) || "";
    return t ? t.trim().split(/\s+/).filter(Boolean).length : 0;
  }, [textOut, refineText]);

  const filteredDictionaryWords = useMemo(() => {
    const q = dictionaryQuery.trim().toLowerCase();
    if (!q) return dictionaryWords;
    return dictionaryWords.filter((word) => word.toLowerCase().includes(q));
  }, [dictionaryWords, dictionaryQuery]);

  const runPromptRequest = async ({
    content,
    promptType,
    promptText = "",
    promptId = null,
    preferPromptText = false,
  }) => {
    if (isTranscribeOnlyPrompt(promptType)) return { text: content };

    const extractCompletionText = (response) =>
      response?.data?.completionResultText ||
      response?.data?.Data?.completionResultText ||
      response?.data?.text ||
      response?.data?.Data?.text ||
      content;

    const explicitPromptText = String(promptText || "").trim();

    if (preferPromptText && explicitPromptText) {
      const res = await axios.post(
        CHAT_COMPLETION_V2_URL,
        {
          rawText: content,
          promptId: 0,
          promptText: explicitPromptText,
          userPreference: "User",
        },
        { withCredentials: true }
      );

      return { text: extractCompletionText(res) };
    }

    const matchingPrompt = promptOptions.find((item) => {
      const itemId = String(item?.id || "").trim();
      const requestedId = String(promptId || "").trim();
      if (requestedId && itemId === requestedId) return true;
      return normalizePromptLabel(item?.label) === normalizePromptLabel(promptType);
    });

    const resolvedPromptId = String(
      matchingPrompt?.id ?? promptId ?? ""
    ).trim();
    const hasBackendPromptId =
      resolvedPromptId.length > 0 &&
      !resolvedPromptId.includes("-") &&
      /^\d+$/.test(resolvedPromptId);
    const resolvedPromptText =
      String(matchingPrompt?.promptText || explicitPromptText || "").trim() ||
      getFallbackPromptText(promptType);

    const res = await axios.post(
      CHAT_COMPLETION_V2_URL,
      {
        rawText: content,
        promptId: hasBackendPromptId ? Number(resolvedPromptId) : 0,
        promptText: hasBackendPromptId ? null : resolvedPromptText || null,
        userPreference: "User",
      },
      { withCredentials: true }
    );

    return { text: extractCompletionText(res) };
  };

  const generateHistoryHeading = async (content) => {
    const headingInstruction =
      "Generate a concise heading (max 3 words) for this transcript. Return heading text only.";
    try {
      const { text } = await runPromptRequest({
        content,
        promptType: "Ask GPT",
        promptText: headingInstruction,
        promptId: null,
        preferPromptText: true,
      });
      const normalized = String(text || "")
        .replace(/^["'`]+|["'`]+$/g, "")
        .replace(/\s+/g, " ")
        .trim();
      return normalized.slice(0, 70) || "Untitled";
    } catch {
      return (
        content
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 6)
          .join(" ") || "Untitled"
      );
    }
  };

  const addTranscriptToHistory = async (content) => {
    const text = String(content || "").trim();
    if (!text) return;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const promptInfo = activePromptInfoRef.current;
    const baseEntry = {
      id,
      title: "Generating title...",
      content: text,
      wordCount: text.split(/\s+/).filter(Boolean).length,
      createdAt: new Date().toISOString(),
      aiProcessed: Boolean(promptInfo?.aiProcessed),
      promptLabel: promptInfo?.label || "",
      promptIcon: promptInfo?.icon || "",
      promptBgColor: promptInfo?.bgColor || "",
    };

    activeHistoryEntryIdRef.current = id;
    setTranscriptHistory((prev) => [baseEntry, ...prev].slice(0, MAX_HISTORY_ITEMS));
    const heading = await generateHistoryHeading(text);
    setTranscriptHistory((prev) =>
      prev.map((item) => (item.id === id ? { ...item, title: heading || "Untitled" } : item))
    );
  };

  const updateActiveHistoryEntry = (content) => {
    const text = String(content || "").trim();
    const activeId = activeHistoryEntryIdRef.current;
    if (!activeId || !text) return false;

    const promptInfo = activePromptInfoRef.current;
    setTranscriptHistory((prev) =>
      prev.map((item) =>
        item.id === activeId
          ? {
            ...item,
            content: text,
            wordCount: text.split(/\s+/).filter(Boolean).length,
            ...(promptInfo
              ? {
                aiProcessed: Boolean(promptInfo.aiProcessed),
                promptLabel: promptInfo.label || "",
                promptIcon: promptInfo.icon || "",
                promptBgColor: promptInfo.bgColor || "",
              }
              : {}),
          }
          : item
      )
    );
    return true;
  };

  const applyPrompt = async ({
    content,
    promptType,
    promptText = "",
    promptId = null,
    outputType = type,
  }) => {
    if (!content?.trim()) {
      toast.error("No text to apply prompt.");
      return;
    }

    const matchedPrompt =
      (promptId &&
        promptOptions.find((p) => String(p.id) === String(promptId))) ||
      promptOptions.find(
        (p) => normalizePromptLabel(p.label) === normalizePromptLabel(promptType)
      ) ||
      null;
    const isRawTranscribe = isTranscribeOnlyPrompt(promptType);
    activePromptInfoRef.current = isRawTranscribe
      ? { aiProcessed: false, label: "", icon: "", bgColor: "" }
      : {
          aiProcessed: true,
          label: matchedPrompt?.label || String(promptType || ""),
          icon: matchedPrompt?.icon || "",
          bgColor: matchedPrompt?.bgColor || "",
        };

    // If Transcribe Only, show raw transcript
    if (isRawTranscribe) {
      setRefineText("");
      if (outputType === "translation") {
        setTranscription("");
        setTranslation(content);
      } else {
        setTranslation("");
        setTranscription(content);
      }
      if (!content) {
        toast.info("No text to copy.");
        return;
      }

      setLatestAiText(content);
      try {
        await navigator.clipboard.writeText(content);
        toast.success("Copied to clipboard.");
      } catch (error) {
        toast.error("Copy failed.");
      }
      return

    }

    setApplyingPrompt(true);

    try {
      const { text } = await runPromptRequest({
        content,
        promptType,
        promptText,
        promptId,
      });

      // show ONLY refined
      setTranscription("");
      setTranslation("");
      setRefineText(text || "");
      // setLatestAiText(text || "");


      setLatestAiText(text);
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard.");
      } catch (error) {
        toast.error("Copy failed.");
      }
    } catch (err) {
      toast.error(formatAppError(err, { feature: "prompt" }));

      // fallback to raw if refine fails
      setRefineText("");
      if (outputType === "translation") {
        setTranscription("");
        setTranslation(content);
      } else {
        setTranslation("");
        setTranscription(content);
      }
    } finally {
      setApplyingPrompt(false);
    }

    // if (hastext) {
    //   toast.success("Settings applied successfully!");
    // }
  };

  // Hooks
  const sessionModeRef = useRef("transcription");

  useEffect(() => {
    targetLanguageRef.current = targetLanguage;
  }, [targetLanguage]);

  // Centralized handler for "free tier minutes exhausted" signals. Invoked
  // once per recording from either:
  //   • the preflight gate in startUnified (cached usage already maxed)
  //   • the realtime hook's onUsageLimit (relay rejected via 1008 / error frame)
  // Uses a single toastId so duplicates are deduped at the UI layer too.
  const handleUsageLimitBlocked = ({ message, source } = {}) => {
    setRecording(false);
    setRecordingPaused(false);
    setVoiceLevel(0);
    setElapsedSeconds(0);
    setFinalizing(false);
    pendingRtRetryRef.current = null;
    setRetryPayload(null);
    setRetryOnlyMode(false);
    setProcessingHaltedMode(false);

    const resetLabel = usage?.resetsOn && usage.resetsOn !== "—" ? usage.resetsOn : null;
    toast.error(
      <div>
        <div style={{ fontWeight: 600 }}>
          Weekly Free plan minutes are exhausted
        </div>
        <div style={{ marginTop: 4, fontWeight: 400, opacity: 0.9 }}>
          {resetLabel
            ? `Your minutes will reset on ${resetLabel}.`
            : "Your minutes will reset soon."}
        </div>
      </div>,
      {
        toastId: "usage-limit-exceeded",
        autoClose: 6000,
      }
    );

    // Pull fresh usage so the banner in AppTopBar flips to "limit-reached"
    // immediately, giving the user the upgrade CTA.
    refresh({ force: true });

    trackEvent("transcription_blocked_usage_limit", {
      source: source || "server",
      input_mode: type,
    });
  };

  const rt = useRealtimeTranscription({
    wsUrl: SOCKET_BASE_URL,
    sessionModeRef,
    targetLanguageRef,
    formatterEnabledRef,
    dictionaryWordsRef,
    onReconnecting: () => {
      toast.info("Connection interrupted — reconnecting…");
    },
    onReconnected: () => {
      toast.success("Reconnected.");
    },
    onFinalText: async (t) => {
      try {
        pendingRtRetryRef.current = null;
        const lockedPrompt = promptAtStartRef.current || {
          label: "Transcribe Only",
          promptText: "",
          id: null,
        };

        if (!isTranscribeOnlyPrompt(lockedPrompt.label)) {
          setTranscription("");
          setRefineText("");
        }

        await applyPrompt({
          content: t,
          promptType: lockedPrompt.label,
          promptText: lockedPrompt.promptText,
          promptId: lockedPrompt.id,
          outputType: "transcription",
        });
      } finally {
        setFinalizing(false);
      }
    },
    onFinalTranslation: async (t) => {
      try {
        pendingRtRetryRef.current = null;
        const lockedPrompt = promptAtStartRef.current || {
          label: "Transcribe Only",
          promptText: "",
          id: null,
        };

        if (isTranscribeOnlyPrompt(lockedPrompt.label)) {
          setRefineText("");
          setTranscription("");
          setTranslation(t);
          setLatestAiText(t);
          try {
            await navigator.clipboard.writeText(t);
            toast.success("Copied to clipboard.");
          } catch { toast.error("Copy failed."); }
        } else {
          setTranscription("");
          setRefineText("");
          await applyPrompt({
            content: t,
            promptType: lockedPrompt.label,
            promptText: lockedPrompt.promptText,
            promptId: lockedPrompt.id,
            outputType: "translation",
          });
        }
      } finally {
        setFinalizing(false);
      }
    },
    onError: (m) => {
      if (m) toast.error(m, { toastId: "rt-error" });
      setFinalizing(false);
      if (pendingRtRetryRef.current) {
        setRetryPayload(pendingRtRetryRef.current.blob ? {
          blob: pendingRtRetryRef.current.blob,
          promptType: pendingRtRetryRef.current.promptType || selected?.label,
          promptText: pendingRtRetryRef.current.promptText || selected?.promptText || "",
          promptId: pendingRtRetryRef.current.promptId || selected?.id || null,
        } : null);
        setRetryOnlyMode(true);
        setControlTrayOpen(true);
      }
    },
    onUsageLimit: handleUsageLimitBlocked,
    onLevelChange: (next) =>
      setVoiceLevel((prev) => prev * 0.65 + Math.max(0, Number(next) || 0) * 0.35),
  });


  const fileTx = useFileTranslation({
    uploadUrl: FILE_TRANSCRIPT_URL,
    onText: (t) => {
      setTranslation("");
      setTranscription(t);
    },
    onError: (m) => { if (m) toast.error(m, { toastId: "file-tx-error" }); },
    onLoading: (v) => setLoading(v),
    onLevelChange: (next) =>
      setVoiceLevel((prev) => prev * 0.65 + Math.max(0, Number(next) || 0) * 0.35),
  });

  const resetOutputs = () => {
    setTranscription("");
    setTranslation("");
    setRefineText("");
  };

  const handleCopyText = async () => {
    if (!currentOutputText) {
      toast.info("No text to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(currentOutputText);
      // toast.success("Copied to clipboard.");
    } catch (error) {
      toast.error("Copy failed.");
    }
  };

  const handleDeleteText = () => {
    if (!currentOutputText) {
      toast.info("Nothing to clear.");
      return;
    }

    setTranscription("");
    setTranslation("");
    setRefineText("");
    activeHistoryEntryIdRef.current = null;
    if (historyUpdateTimerRef.current) {
      clearTimeout(historyUpdateTimerRef.current);
      historyUpdateTimerRef.current = null;
    }
    toast.success("Cleared.");
  };

  const handleEditorTextChange = (nextValue) => {
    const nextText = String(nextValue || "");
    if (refineText?.length > 0) {
      setRefineText(nextText);
      return;
    }
    if (transcription?.length > 0 || type === "transcription") {
      setTranscription(nextText);
      return;
    }
    setTranslation(nextText);
  };

  const handleHistoryCopy = async (content) => {
    if (!content?.trim()) return;
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const handleHistoryDelete = (id) => {
    if (activeHistoryEntryIdRef.current === id) {
      activeHistoryEntryIdRef.current = null;
    }
    setTranscriptHistory((prev) => prev.filter((item) => item.id !== id));
  };

  const handleHistoryApplyPrompt = async (id, prompt) => {
    const target = transcriptHistory.find((item) => item.id === id);
    if (!target) return;
    const { text } = await runPromptRequest({
      content: target.content,
      promptType: prompt?.label || "Transcribe Only",
      promptText: prompt?.promptText || "",
      promptId: prompt?.id || null,
    });
    const nextText = String(text || target.content || "").trim();
    const nextTitle = await generateHistoryHeading(nextText);
    const isRawTranscribe = isTranscribeOnlyPrompt(prompt?.label);
    activeHistoryEntryIdRef.current = id;
    setTranscriptHistory((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
            ...item,
            content: nextText,
            title: nextTitle || item.title,
            wordCount: nextText.split(/\s+/).filter(Boolean).length,
            aiProcessed: !isRawTranscribe,
            promptLabel: isRawTranscribe ? "" : prompt?.label || "",
            promptIcon: isRawTranscribe ? "" : prompt?.icon || "",
            promptBgColor: isRawTranscribe ? "" : prompt?.bgColor || "",
          }
          : item
      )
    );
    toast.success("Prompt applied to history note.");
  };

  const saveDictionaryWords = async (words) => {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}api/setup/special-words`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ words }),
      }
    );

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.error || "Failed to save dictionary words");
    }

    return Array.isArray(json?.words) ? json.words : words;
  };

  const handleAddDictionaryWord = async () => {
    const nextWord = dictionaryQuery.trim();
    if (!nextWord) {
      toast.info("Enter a word to add.");
      return;
    }

    const exists = dictionaryWords.some(
      (word) => word.toLowerCase() === nextWord.toLowerCase()
    );
    if (exists) {
      toast.info("Word already exists.");
      return;
    }

    const nextWords = [...dictionaryWords, nextWord];
    try {
      setDictionarySaving(true);
      setDictionaryError("");
      const savedWords = await saveDictionaryWords(nextWords);
      setDictionaryWords(savedWords);
      setDictionaryQuery("");
      toast.success("Word added.");
    } catch (err) {
      console.error("Dictionary save error:", err);
      setDictionaryError(err.message || "Failed to save dictionary");
      toast.error(err.message || "Failed to save dictionary");
    } finally {
      setDictionarySaving(false);
    }
  };

  const handleRemoveDictionaryWord = async (wordToRemove) => {
    const nextWords = dictionaryWords.filter((word) => word !== wordToRemove);

    try {
      setDictionarySaving(true);
      setDictionaryError("");
      const savedWords = await saveDictionaryWords(nextWords);
      setDictionaryWords(savedWords);
      toast.success("Word removed.");
    } catch (err) {
      console.error("Dictionary delete error:", err);
      setDictionaryError(err.message || "Failed to update dictionary");
      toast.error(err.message || "Failed to update dictionary");
    } finally {
      setDictionarySaving(false);
    }
  };

  const fetchDictionaryWords = async (signal) => {
    try {
      setDictionaryLoading(true);
      setDictionaryError("");

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}api/setup/special-words`,
        {
          method: "GET",
          credentials: "include",
          signal,
        }
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json?.error || "Failed to fetch dictionary words");
      }

      const words = Array.isArray(json?.words) ? json.words : [];
      setDictionaryWords(words);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Dictionary fetch error:", err);
      setDictionaryWords([]);
      setDictionaryError(err.message || "Failed to load dictionary");
    } finally {
      setDictionaryLoading(false);
    }
  };

  const fetchPromptOptions = async (signal) => {
    try {
      const res = await fetch(PROMPTS_URL, {
        method: "GET",
        credentials: "include",
        signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || "Failed to fetch prompts");
      }

      const prompts = Array.isArray(json?.prompts) ? json.prompts : [];
      if (!prompts.length) return;

      const mapped = mapPromptItemsForDisplay(prompts);

      if (!mapped.length) return;

      const apiDefault = mapped.find((p) => p.isDefaultPrompt);
      setPromptOptions(mapped);
      setDefaultPromptId(String(apiDefault?.id || ""));
      setSelected((prev) => {
        if (apiDefault) return apiDefault;
        const byCurrent = mapped.find((p) => p.label === prev?.label);
        if (byCurrent) return byCurrent;
        const transcribeOnly = mapped.find((p) => p.label === "Transcribe Only");
        return transcribeOnly || mapped[0];
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Prompt fetch error:", err);
    }
  };

  const handleMakeDefaultPrompt = (prompt) => {
    if (!prompt?.id) return;
    const nextPromptId = String(prompt.id);
    const optimistic = sortPromptOptionsForDisplay(
      promptOptions.map((item) => ({
        ...item,
        id: String(item.id),
        isDefaultPrompt: String(item.id) === nextPromptId,
      }))
    );

    setPromptOptions(optimistic);
    setDefaultPromptId(nextPromptId);
    setSelected({
      ...prompt,
      id: nextPromptId,
      isDefaultPrompt: true,
    });

    (async () => {
      try {
        const res = await axios.post(
          `${BACKEND}api/setup/prompts/default`,
          { promptId: nextPromptId },
          { withCredentials: true }
        );

        const prompts = Array.isArray(res?.data?.prompts) ? res.data.prompts : [];
        if (prompts.length) {
          const mapped = mapPromptItemsForDisplay(prompts);
          const serverDefault = mapped.find((item) => item.isDefaultPrompt);
          const resolved =
            serverDefault && String(serverDefault.id) === nextPromptId
              ? mapped
              : applyDefaultPromptToMapped(mapped, nextPromptId);

          setPromptOptions(resolved);
          const nextDefault = resolved.find((item) => item.isDefaultPrompt) || resolved[0];
          if (nextDefault) {
            setDefaultPromptId(String(nextDefault.id));
            setSelected(nextDefault);
          }
        } else {
          setPromptOptions(optimistic);
          setDefaultPromptId(nextPromptId);
          setSelected({
            ...prompt,
            id: nextPromptId,
            isDefaultPrompt: true,
          });
        }

        try {
          window.localStorage.setItem(DEFAULT_PROMPT_KEY, nextPromptId);
        } catch { }
        toast.success(`"${prompt.label}" set as default.`);
      } catch (err) {
        setPromptOptions((prev) => applyDefaultPromptToMapped(prev, defaultPromptId));
        const message =
          formatAppError(err, { feature: "prompt" });
        toast.error(message);
      }
    })();
  };

  const startUnified = async () => {
    // Preflight: short-circuit when cached usage already says the weekly free
    // budget is spent. Avoids the mic prompt, the WS handshake and the toast
    // storm that would otherwise come back from the relay.
    if (
      usage &&
      !usage.isPro &&
      typeof usage.totalMin === "number" &&
      usage.totalMin > 0 &&
      typeof usage.usedMin === "number" &&
      usage.usedMin >= usage.totalMin
    ) {
      handleUsageLimitBlocked({ message: USAGE_LIMIT_MESSAGE, source: "preflight" });
      return false;
    }

    try {
      resetOutputs();
      setElapsedSeconds(0);
      setVoiceLevel(0);
      setRetryOnlyMode(false);
      setProcessingHaltedMode(false);
      setRetryPayload(null);
      pendingRtRetryRef.current = null;
      usedFileFallbackRef.current = false;
      setRecordingPaused(false);
      // Flip recording on synchronously so the elapsed timer starts the instant
      // the user taps the mic. Mic acquisition + WebSocket bootstrap still run
      // below; if they fail, the catch block resets recording to false.
      setRecording(true);

      // ✅ LOCK PROMPT HERE (MAIN FIX)
      promptAtStartRef.current = {
        label: selected?.label || "Transcribe Only",
        promptText: selected?.promptText || "",
        id: selected?.id || null,
      };

      trackEvent("dictation_started", {
        input_mode: type,
        prompt_type: selected?.label || "Transcribe Only",
      });

      sessionModeRef.current = type;
      if (type === "transcription") {
        try {
          await rt.start();
        } catch (rtErr) {
          // Mirror Android: realtime init failure → fall back to file-based transcription
          console.warn("[realtime] init failed, falling back to file mode:", rtErr);
          usedFileFallbackRef.current = true;
          await fileTx.start();
        }
      } else {
        await rt.start();
      }

      return true;
    } catch (e) {
      setRecording(false);
      setRecordingPaused(false);
      setVoiceLevel(0);
      return false;
    }
  };

  const stopUnified = async ({
    closeTray = true,
    showFinalizing = true,
    commit = true,
    instantClose = false,
  } = {}) => {
    let keepTrayOpen = false;
    if (closeTray && instantClose) {
      setInstantTrayCollapse(true);
      setControlTrayOpen(false);
    }

    // If the user clicks Stop/Cancel before startUnified has finished bootstrapping
    // (mic + WebSocket session.ready), wait for it. Otherwise rt.stop() races against
    // a half-initialized session and throws "WebSocket not open", which surfaces as
    // "Transcription is temporarily unavailable." Waiting also prevents the elapsed
    // timer from flipping on after the user thought they had already stopped.
    if (startInFlightRef.current) {
      try { await startInFlightRef.current; } catch { }
    }

    try {
      setRecording(false);
      setRecordingPaused(false);
      setVoiceLevel(0);
      setElapsedSeconds(0);

      setFinalizing(showFinalizing);

      if ((type === "transcription" || type === "translation") && !usedFileFallbackRef.current) {
        const isOffline =
          commit &&
          typeof navigator !== "undefined" &&
          navigator &&
          navigator.onLine === false;
        const rtResult = await rt.stop({ commit: isOffline ? false : commit });
        const blankAudio = rtResult?.blankAudio;

        if (blankAudio?.isBlank) {
          pendingRtRetryRef.current = null;
          setRetryPayload(null);
          setRetryOnlyMode(false);
          setProcessingHaltedMode(false);
          setFinalizing(false);
          toast.info("No speech detected. Please try again.");
        } else {
          pendingRtRetryRef.current = {
            blob: rtResult?.blob || null,
            promptType: selected?.label,
            promptText: selected?.promptText || "",
            promptId: selected?.id || null,
          };
        }

        if (!blankAudio?.isBlank && isOffline) {
          setFinalizing(false);
          toast.error("No internet connection. Retry to upload your recording.");
          setRetryPayload(rtResult?.blob ? {
            blob: rtResult.blob,
            promptType: selected?.label,
            promptText: selected?.promptText || "",
            promptId: selected?.id || null,
          } : null);
          setRetryOnlyMode(true);
          setControlTrayOpen(true);
          keepTrayOpen = true;
        } else if (!blankAudio?.isBlank && rtResult?.error) {
          setFinalizing(false);
          // Usage-limit failures are already surfaced by handleUsageLimitBlocked
          // (single toast + banner refresh) — don't show a second toast or
          // offer a retry that will fail with the same error.
          if (rtResult.error?.errorCode === USAGE_LIMIT_ERROR_CODE) {
            pendingRtRetryRef.current = null;
            setRetryPayload(null);
            setRetryOnlyMode(false);
          } else {
            toast.error(formatAppError(rtResult.error, { feature: "transcription" }));
            setRetryPayload(rtResult?.blob ? {
              blob: rtResult.blob,
              promptType: selected?.label,
              promptText: selected?.promptText || "",
              promptId: selected?.id || null,
            } : null);
            setRetryOnlyMode(true);
            setControlTrayOpen(true);
            keepTrayOpen = true;
          }
        } else if (!commit || blankAudio?.isBlank) {
          setFinalizing(false);
        } else if (!isOffline) {
          const completionProps = {
            input_mode: type,
            prompt_type: selected?.label || "Transcribe Only",
            elapsed_seconds: elapsedSeconds,
          };
          trackEvent("dictation_completed", completionProps);
          trackFirstDictationCompletedOnce(completionProps);
          refreshDebounced();
        }
      } else {
        if (commit) {
          const isNumericId = selected?.id && /^\d+$/.test(String(selected.id));
          const translationPromptId = isNumericId ? Number(selected.id) : -100;
          const translationUrl = `${FILE_TRANSLATE_URL}&promptId=${translationPromptId}&userPreference=User`;

          const result = await fileTx.stop({
            uploadUrl: type === "translation" ? translationUrl : FILE_TRANSCRIPT_URL,
            uploadType: type === "translation" ? "translation" : "transcription",
          });
          if (result?.blankAudio) {
            toast.info("No speech detected. Silent audio was not uploaded.");
            setRetryOnlyMode(false);
            setProcessingHaltedMode(false);
            setRetryPayload(null);
          } else if (!result?.ok) {
            setRetryPayload({
              blob: result?.blob || null,
              promptType: selected?.label,
              promptText: selected?.promptText || "",
              promptId: selected?.id || null,
              uploadUrl: type === "translation" ? translationUrl : FILE_TRANSCRIPT_URL,
              uploadType: type === "translation" ? "translation" : "transcription",
            });
            setRetryOnlyMode(true);
            setProcessingHaltedMode(false);
            setControlTrayOpen(true);
            keepTrayOpen = true;
          } else if (type === "translation") {
            // ZeroTyping API handled translation + prompt in one call
            const whisperText = result?.text || "";
            const finalText = result?.finalText || whisperText;
            if (isTranscribeOnlyPrompt(selected?.label)) {
              setRefineText("");
              setTranscription("");
              setTranslation(whisperText);
              setLatestAiText(whisperText);              // ← ADD
              if (whisperText) {
                try {
                  await navigator.clipboard.writeText(whisperText);
                  toast.success("Copied to clipboard.");
                } catch { toast.error("Copy failed."); }
              }
            } else {
              setTranscription(whisperText);
              setTranslation("");
              setRefineText(finalText);
              setLatestAiText(finalText);
              if (finalText) {
                try {
                  await navigator.clipboard.writeText(finalText);
                  toast.success("Copied to clipboard.");
                } catch { toast.error("Copy failed."); }
              }
            }
          } else {
            await applyPrompt({
              content: result?.text || "",
              promptType: selected?.label || "Transcribe Only",
              promptText: selected?.promptText || "",
              promptId: selected?.id || null,
              outputType: type,
            });
            setRetryOnlyMode(false);
            setProcessingHaltedMode(false);
            setRetryPayload(null);
            const completionProps = {
              input_mode: type,
              prompt_type: selected?.label || "Transcribe Only",
              elapsed_seconds: elapsedSeconds,
            };
            trackEvent("dictation_completed", completionProps);
            trackFirstDictationCompletedOnce(completionProps);
            refreshDebounced();
          }
        } else {
          fileTx.cancel();
          setRetryOnlyMode(false);
          setProcessingHaltedMode(false);
          setRetryPayload(null);
          pendingRtRetryRef.current = null;
        }
        setFinalizing(false);
      }
      if (closeTray && !instantClose && !keepTrayOpen) {
        setControlTrayOpen(false);
      }
    } catch (e) {
      setFinalizing(false);
    }
  };

  const formatElapsed = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleMicButtonClick = async () => {
    if (Date.now() < ignoreMicTapUntilRef.current) return;
    if (loading || finalizing) return;
    // While a start is already in-flight, ignore additional taps. Without this,
    // `recording` is still false during init and the !recording branch would
    // re-enter, spawning a second concurrent rt.start().
    if (startInFlightRef.current) return;

    if (!recording) {
      setInstantTrayCollapse(false);
      setRetryOnlyMode(false);
      setProcessingHaltedMode(false);
      setRetryPayload(null);
      pendingRtRetryRef.current = null;
      // Open tray immediately for instant visual feedback; mic acquisition
      // and WebSocket bootstrap run in parallel. Early audio captured before
      // session.ready is buffered in earlyAudioBuffer and flushed when ready,
      // so transcription is not lost.
      setControlTrayOpen(true);
      const startPromise = startUnified();
      startInFlightRef.current = startPromise;
      try {
        const started = await startPromise;
        if (!started) setControlTrayOpen(false);
      } finally {
        if (startInFlightRef.current === startPromise) {
          startInFlightRef.current = null;
        }
      }
      return;
    }

    if (!controlTrayOpen) setInstantTrayCollapse(false);
    setControlTrayOpen((prev) => !prev);
  };

  const handleQuickStart = async () => {
    if (Date.now() < ignoreMicTapUntilRef.current) return;
    if (recording || loading || finalizing) return;
    if (startInFlightRef.current) return;

    setInstantTrayCollapse(false);
    setRetryOnlyMode(false);
    setProcessingHaltedMode(false);
    setRetryPayload(null);
    pendingRtRetryRef.current = null;
    const startPromise = startUnified();
    startInFlightRef.current = startPromise;
    try {
      await startPromise;
    } finally {
      if (startInFlightRef.current === startPromise) {
        startInFlightRef.current = null;
      }
    }
  };

  const handleQuickStop = () => {
    stopUnified({ closeTray: true });
  };

  const handlePauseResume = () => {
    if (!recording) return;

    if (recordingPaused) {
      if (type === "transcription" || type === "translation") rt.resume();
      else fileTx.resume();
      setRecordingPaused(false);
      return;
    }

    if (type === "transcription" || type === "translation") rt.pause();
    else fileTx.pause();
    setVoiceLevel(0);
    setRecordingPaused(true);
  };

  const handleRestart = async () => {
    if (!recording) return;

    resetOutputs();
    setElapsedSeconds(0);
    setVoiceLevel(0);
    setRetryOnlyMode(false);
    setProcessingHaltedMode(false);
    setRetryPayload(null);
    pendingRtRetryRef.current = null;
    setRecordingPaused(false);

    try {
      if ((type === "transcription" || type === "translation") && !usedFileFallbackRef.current) {
        await rt.stop({ commit: false });
        try {
          await rt.start();
        } catch (rtErr) {
          console.warn("[realtime] init failed on restart, falling back to file mode:", rtErr);
          usedFileFallbackRef.current = true;
          await fileTx.start();
        }
      } else {
        fileTx.cancel();
        await fileTx.start();
      }
    } catch (e) {
      setRecording(false);
      setControlTrayOpen(false);
    }
  };

  const handleRetryUpload = async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      toast.error("No internet connection. Please reconnect and retry.");
      setRetryOnlyMode(true);
      setControlTrayOpen(true);
      return;
    }

    if (!retryPayload?.blob) {
      toast.info("No recording available. Please try a new recording.");
      setRetryOnlyMode(false);
      setControlTrayOpen(false);
      return;
    }

    const result = await fileTx.retryUpload({
      blob: retryPayload.blob,
      uploadUrl: retryPayload.uploadUrl,
      uploadType: retryPayload.uploadType,
    });

    if (result?.ok) {
      if (retryPayload.uploadType === "translation") {
        // ZeroTyping API handled translation + prompt in one call
        const whisperText = result?.text || "";
        const finalText = result?.finalText || whisperText;
        if (isTranscribeOnlyPrompt(retryPayload.promptType)) {
          setRefineText("");
          setTranscription("");
          setTranslation(whisperText);
          setLatestAiText(whisperText);                          // ← ADD
          if (whisperText) {
            try {
              await navigator.clipboard.writeText(whisperText);
              toast.success("Copied to clipboard.");
            } catch { toast.error("Copy failed."); }

          }
        } else {
          setTranscription(whisperText);
          setTranslation("");
          setRefineText(finalText);
          setLatestAiText(finalText);                            // ← ADD
          if (finalText) {
            try {
              await navigator.clipboard.writeText(finalText);
              toast.success("Copied to clipboard.");
            } catch { toast.error("Copy failed."); }
          }
        }
      } else {
        await applyPrompt({
          content: result?.text || "",
          promptType: retryPayload.promptType || "Transcribe Only",
          promptText: retryPayload.promptText || "",
          promptId: retryPayload.promptId || null,
          outputType: "transcription",
        });
      }
      setRetryOnlyMode(false);
      setProcessingHaltedMode(false);
      setRetryPayload(null);
      pendingRtRetryRef.current = null;
      setControlTrayOpen(false);
    } else {
      setRetryOnlyMode(true);
      setControlTrayOpen(true);
    }
  };

  const handleCancelProcessing = () => {
    ignoreMicTapUntilRef.current = Date.now() + 400;
    try {
      rt.teardown();
    } catch { }
    setFinalizing(false);
    setInstantTrayCollapse(false);
    setControlTrayOpen(true);
    setRetryOnlyMode(false);
    setProcessingHaltedMode(true);
    setRetryPayload(null);
    pendingRtRetryRef.current = null;
    setVoiceLevel(0);
    toast.info("Processing halted.");
  };

  const handleDismissRetry = () => {
    setRetryOnlyMode(false);
    setRetryPayload(null);
    pendingRtRetryRef.current = null;
    setControlTrayOpen(false);
  };

  const handleDismissProcessingHalted = () => {
    setProcessingHaltedMode(false);
    setControlTrayOpen(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TRANSCRIPT_HISTORY_KEY, JSON.stringify(transcriptHistory));
  }, [transcriptHistory]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!defaultPromptId) return;
    window.localStorage.setItem(DEFAULT_PROMPT_KEY, defaultPromptId);
  }, [defaultPromptId]);

  useEffect(() => {
    if (!currentOutputText || recording || finalizing || loading) return;
    if (lastHistoryContentRef.current === currentOutputText) return;
    lastHistoryContentRef.current = currentOutputText;
    if (historyUpdateTimerRef.current) {
      clearTimeout(historyUpdateTimerRef.current);
      historyUpdateTimerRef.current = null;
    }
    historyUpdateTimerRef.current = setTimeout(() => {
      const updated = updateActiveHistoryEntry(currentOutputText);
      if (!updated) {
        addTranscriptToHistory(currentOutputText);
      }
      historyUpdateTimerRef.current = null;
    }, HISTORY_UPDATE_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOutputText, recording, finalizing, loading]);

  useEffect(
    () => () => {
      if (historyUpdateTimerRef.current) {
        clearTimeout(historyUpdateTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // if user switches type while recording, stop cleanly
  useEffect(() => {
    if (!recording) return;
    stopUnified({ closeTray: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!recording || recordingPaused) return;
    const timer = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [recording, recordingPaused]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchDictionaryWords(ctrl.signal);
    fetchPromptOptions(ctrl.signal);

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure server session is refreshed when /app loads.
  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}api/auth/refresh-session`, {
          method: "POST",
          credentials: "include",
          signal: ctrl.signal,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("[refresh-session] error:", err);
        }
      }
    })();

    return () => ctrl.abort();
  }, []);

  return (
    <>
      <Sidebar
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
        openSidebarMobile={openSidebarMobile}
        setOpenSidebarMobile={setOpenSidebarMobile}
      />

      <div
        className={`w-full bg-white flex flex-col text-black overflow-hidden transition-all duration-500 ${openSidebar ? "lg:pl-[200px] xl:pl-[260px]" : "lg:pl-16"
          }`}
      >
        <AppTopBar onOpenSidebar={() => setOpenSidebarMobile(true)} />

        <div className="bg-white flex flex-col lg:gap-5 md:gap-4 gap-3 md:p-8 p-4 min-h-screen overflow-hidden">
          <div className="flex lg:flex-row flex-col items-stretch gap-8 lg:h-[60vh] h-max lg:min-h-max min-h-max mt-6 max-w-screen-2xl w-full mx-auto">
            <MainTextBoxPanel
              type={type}
              setType={setType}
              targetLanguage={targetLanguage}
              onTargetLanguageChange={setTargetLanguage}
              wordCount={wordCount}
              textOut={textOut}
              refineText={refineText}
              recording={recording}
              onCopy={handleCopyText}
              latestAiText={latestAiText}
              onDelete={handleDeleteText}
              onTextChange={handleEditorTextChange}
              formatterEnabled={formatterEnabled}
              onToggleFormatter={handleToggleFormatter}
              recordingTrayProps={{
                loading,
                finalizing,
                recording,
                controlTrayOpen,
                retryOnlyMode,
                processingHaltedMode,
                instantTrayCollapse,
                recordingPaused,
                elapsedTime: formatElapsed(elapsedSeconds),
                voiceLevel,
                onMicClick: handleMicButtonClick,
                onQuickStart: handleQuickStart,
                onQuickStop: handleQuickStop,
                onStop: () => stopUnified({ closeTray: true }),
                onRestart: handleRestart,
                onPauseResume: handlePauseResume,
                onRetry: handleRetryUpload,
                onRetryCancel: handleDismissRetry,
                onProcessingCancel: handleCancelProcessing,
                onProcessingHaltedCancel: handleDismissProcessingHalted,
                onCancel: () =>
                  stopUnified({
                    closeTray: true,
                    showFinalizing: false,
                    commit: false,
                    instantClose: true,
                  }),
              }}
            />

            <div className="lg:flex-[1.08] lg:h-full h-max flex flex-col gap-4 overflow-visible">
              <UsagePreferenceCard type={type} onTypeChange={setType} disabled={recording} className="hidden lg:block" />

              {type === "translation" && (
                <OutputLanguageCard
                  value={targetLanguage}
                  onChange={setTargetLanguage}
                  disabled={recording}
                  className="hidden lg:block"
                />
              )}

              <SettingsPanel
                dropdownRef={dropdownRef}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                selected={selected}
                setSelected={setSelected}
                promptOptions={promptOptions}
                onMakeDefault={handleMakeDefaultPrompt}
                defaultPromptId={defaultPromptId}
                hastext={hastext}
                applyLoading={applyingPrompt}
                onApplySettings={() =>
                  applyPrompt({
                    content: (refineText || textOut || "").trim(),
                    promptType: selected?.label || "Transcribe Only",
                    promptText: selected?.promptText || "",
                    promptId: selected?.id || null,
                  })
                }
              />

              <DictionaryPanel
                dictionaryLoading={dictionaryLoading}
                dictionarySaving={dictionarySaving}
                dictionaryQuery={dictionaryQuery}
                setDictionaryQuery={setDictionaryQuery}
                dictionaryWordsCount={dictionaryWords.length}
                filteredDictionaryWords={filteredDictionaryWords}
                dictionaryError={dictionaryError}
                onRefresh={() => fetchDictionaryWords()}
                onAdd={handleAddDictionaryWord}
                onRemove={handleRemoveDictionaryWord}
              />
            </div>
          </div>

          <TranscriptHistoryPanel
            items={transcriptHistory}
            promptOptions={promptOptions}
            onApplyPrompt={handleHistoryApplyPrompt}
            onDelete={handleHistoryDelete}
            onCopy={handleHistoryCopy}
          />
        </div>
      </div>
    </>
  );
};

export default WebApp;
